/* eslint-disable camelcase */
import { $isTableCellNode, $isTableRowNode, $unmergeCell } from '@lexical/table';
import { MenuItem } from '@mui/material';
import {
  $createParagraphNode,
  $getSelection, $isElementNode, $isParagraphNode, $isRangeSelection, $isTextNode, DEPRECATED_$getNodeTriplet, DEPRECATED_$isGridCellNode, DEPRECATED_$isGridSelection, DEPRECATED_GridCellNode, ElementNode, GridSelection, LexicalEditor,
} from 'lexical';
import React from 'react';
import { useTranslation } from 'react-i18next';
import invariant from '../../../../shared/invariant';

interface MergeCellItemProps {
  editor: LexicalEditor;
  onClose: () => void;
}

function $cellContainsEmptyParagraph(cell: DEPRECATED_GridCellNode): boolean {
  if (cell.getChildrenSize() !== 1) {
    return false;
  }
  const firstChild = cell.getFirstChildOrThrow();
  if (!$isParagraphNode(firstChild) || !firstChild.isEmpty()) {
    return false;
  }
  return true;
}

function $selectLastDescendant(node: ElementNode): void {
  const lastDescendant = node.getLastDescendant();
  if ($isTextNode(lastDescendant)) {
    lastDescendant.select();
  } else if ($isElementNode(lastDescendant)) {
    lastDescendant.selectEnd();
  } else if (lastDescendant !== null) {
    lastDescendant.selectNext();
  }
}

export function computeSelectionCount(selection: GridSelection): {
  columns: number;
  rows: number;
} {
  const selectionShape = selection.getShape();
  return {
    columns: selectionShape.toX - selectionShape.fromX + 1,
    rows: selectionShape.toY - selectionShape.fromY + 1,
  };
}

export default function MergeCellMenuItem(props: MergeCellItemProps) {
  const { editor, onClose } = props;
  const [canMergeCells, setCanMergeCells] = React.useState(false);
  const [canUnmergeCell, setCanUnmergeCell] = React.useState(false);

  function $canUnmerge(): boolean {
    const selection = $getSelection();
    if (
      ($isRangeSelection(selection) && !selection.isCollapsed())
      || (DEPRECATED_$isGridSelection(selection)
        && !selection.anchor.is(selection.focus))
      || (!$isRangeSelection(selection) && !DEPRECATED_$isGridSelection(selection))
    ) {
      return false;
    }
    const [cell] = DEPRECATED_$getNodeTriplet(selection.anchor);
    return cell.getColSpan() > 1 || cell.getRowSpan() > 1;
  }

  // This is important when merging cells as there is no good way to re-merge weird shapes (a result
  // of selecting merged cells and non-merged)
  function isGridSelectionRectangular(selection: GridSelection): boolean {
    const nodes = selection.getNodes();
    const currentRows: Array<number> = [];
    let currentRow = null;
    let expectedColumns = null;
    let currentColumns = 0;
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      if ($isTableCellNode(node)) {
        const row = node.getParentOrThrow();
        invariant(
          $isTableRowNode(row),
          'Expected CellNode to have a RowNode parent',
        );
        if (currentRow !== row) {
          if (expectedColumns !== null && currentColumns !== expectedColumns) {
            return false;
          }
          if (currentRow !== null) {
            expectedColumns = currentColumns;
          }
          currentRow = row;
          currentColumns = 0;
        }
        const colSpan = node.getColSpan();
        for (let j = 0; j < colSpan; j += 1) {
          if (currentRows[currentColumns + j] === undefined) {
            currentRows[currentColumns + j] = 0;
          }
          currentRows[currentColumns + j] += node.getRowSpan();
        }
        currentColumns += colSpan;
      }
    }
    return (
      (expectedColumns === null || currentColumns === expectedColumns)
    && currentRows.every((v) => v === currentRows[0])
    );
  }

  React.useEffect(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      // Merge cells
      if (DEPRECATED_$isGridSelection(selection)) {
        const currentSelectionCounts = computeSelectionCount(selection);
        setCanMergeCells(
          isGridSelectionRectangular(selection)
          && (currentSelectionCounts.columns > 1
            || currentSelectionCounts.rows > 1),
        );
      }
      // Unmerge cell
      setCanUnmergeCell($canUnmerge());
    });
  }, [editor]);

  const mergeTableCellsAtSelection = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (DEPRECATED_$isGridSelection(selection)) {
        const { columns, rows } = computeSelectionCount(selection);
        const nodes = selection.getNodes();
        let firstCell: null | DEPRECATED_GridCellNode = null;
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];
          if (DEPRECATED_$isGridCellNode(node)) {
            if (firstCell === null) {
              node.setColSpan(columns).setRowSpan(rows);
              firstCell = node;
              const isEmpty = $cellContainsEmptyParagraph(node);
              const firstChild = node.getFirstChild();
              if (
                isEmpty
                && $isParagraphNode((firstChild))
              ) {
                firstChild.remove();
              }
            } else if (DEPRECATED_$isGridCellNode(firstCell)) {
              const isEmpty = $cellContainsEmptyParagraph(node);
              if (!isEmpty) {
                firstCell.append(...node.getChildren());
              }
              node.remove();
            }
          }
        }
        if (firstCell !== null) {
          if (firstCell.getChildrenSize() === 0) {
            firstCell.append($createParagraphNode());
          }
          $selectLastDescendant(firstCell);
        }
        onClose();
      }
    });
  };

  const unmergeTableCellsAtSelection = () => {
    editor.update(() => {
      $unmergeCell();
    });
  };

  const { t } = useTranslation('wysiwyg');
  if (canMergeCells) {
    return <MenuItem onClick={() => mergeTableCellsAtSelection()}>{t('table.actions.merge-cells')}</MenuItem>;
  }
  if (canUnmergeCell) {
    return <MenuItem onClick={() => unmergeTableCellsAtSelection()}>{t('table.actions.unmerge-cells')}</MenuItem>;
  }
  return null;
}
